import TabDrawer from '../Reusable/TabDrawer';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SessionTokenSettings from './SessionTokenSettings';
import OauthTokenSettings from './OauthTokenSettings';
import AuthenticationSettings from './AuthenticationSettings';
import AccountLockOutPolicySettings from './AccountLockOutPolicySettings';

/**
 * @parentId authorization-settings
 * @manager View, Edit - Authorization settings
 * @overview View, Edit - Authorization settings
 *   <section>
 *       <p>
 *          Access manangement settings page is the default page the user is navigated on clicking <b>Authorization settings</b> navigation bar menu item. <br/><br/>
 *          Page will show 4 tabs of access management settings.<br/><br/>
 *          1) Session token settings.<br/>
 *          2) Oauth settings.<br/>
 *          3) Authentication settings.<br/>
 *          4) Account lockout settings.<br/>
 *       </p>
 *        <p>Clicking on tab header shows the fields associated to that tab setting.<p/>
 *        <h3>Session token settings</h3>
 *        <p>Table lists all the Session token settings.</p>
 *       <table>
 *           <tr>
 *               <th>Parameter</th>
 *               <th>Default Value</th>
 *               <th>Min</th>
 *               <th>Max</th>
 *           </tr>
 *           <tr>
 *               <td>Max Caching Time (minutes)</td>
 *               <td>3 minutes</td>
 *               <td>1 minutes</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Max Session Time (minutes)</td>
 *               <td>1440 minutes</td>
 *               <td>1 minutes</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Max Idle Time (minutes)</td>
 *               <td>1440 minutes</td>
 *               <td>1 minutes</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Active User Sessions</td>
 *               <td>2</td>
 *               <td>1 minutes</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>session quota exhausted behavior</td>
 *               <td>Destroy Next Expiring</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *       </table>
 *       <br>
 *        <h3>Oauth settings</h3>
 *         <p>This tab displays values to be set for different clients <b>Portal Client (for web portals)</b>, <b>Mobile Client (for mobile)</b>, <b>Open ID Connect_Proof Key for Code Exchange (OIDC_PKCE) (for mobile)</b>, <b>Service Account Client</b>.</p>
 *       <h4>Portal Client (for web portals)</h4>
 *        <table>
 *           <tr>
 *               <th>Parameter</th>
 *               <th>Default Value</th>
 *               <th>Min</th>
 *               <th>Max</th>
 *           </tr>
 *           <tr>
 *               <td>Access Token lifetime (seconds)</td>
 *               <td>600 seconds</td>
 *               <td>60 seconds</td>
 *               <td>3600 seconds</td>
 *           </tr>
 *           <tr>
 *               <td>Refresh token lifetime (seconds)</td>
 *               <td>86400 seconds</td>
 *               <td>600 seconds</td>
 *               <td>172800 seconds</td>
 *           </tr>
 *           <tr>
 *               <td>Authorization code lifetime (seconds)</td>
 *               <td>120 seconds</td>
 *               <td>60 seconds</td>
 *               <td>600 seconds</td>
 *           </tr>
 *           <tr>
 *               <td>Redirection URIs</td>
 *               <td>{{apigee_domain}}/authentication-service/callback, http://localhost</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Token id lifetime (seconds)</td>
 *               <td>3600 seconds</td>
 *               <td>600 seconds</td>
 *               <td>172800 seconds</td>
 *           </tr>
 *       </table>
 *       <br>
 *       <h4>Mobile Client (for mobile)</h4>
 *        <table>
 *           <tr>
 *               <th>Parameter</th>
 *               <th>Default Value</th>
 *               <th>Min</th>
 *               <th>Max</th>
 *           </tr>
 *           <tr>
 *               <td>Access Token lifetime (seconds)</td>
 *               <td>600 seconds</td>
 *               <td>60 seconds</td>
 *               <td>3600 seconds</td>
 *           </tr>
 *           <tr>
 *               <td>Refresh token lifetime (seconds)</td>
 *               <td>172800 seconds</td>
 *               <td>600 seconds</td>
 *               <td>172800 seconds</td>
 *           </tr>
 *           <tr>
 *               <td>Authorization code lifetime (seconds)</td>
 *               <td>120 seconds</td>
 *               <td>60 seconds</td>
 *               <td>600 seconds</td>
 *           </tr>
 *           <tr>
 *               <td>Redirection URIs</td>
 *               <td>{{apigee_domain}}/authentication-service/callback, http://localhost</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Token id lifetime (seconds)</td>
 *               <td>3600 seconds</td>
 *               <td>600 seconds</td>
 *               <td>172800 seconds</td>
 *           </tr>
 *       </table>
 *       <br>
 *       <h4>Open ID Connect_Proof Key for Code Exchange (OIDC_PKCE) (for mobile)</h4>
 *        <table>
 *           <tr>
 *               <th>Parameter</th>
 *               <th>Default Value</th>
 *               <th>Min</th>
 *               <th>Max</th>
 *           </tr>
 *           <tr>
 *               <td>Access Token lifetime (seconds)</td>
 *               <td>600 seconds</td>
 *               <td>60 seconds</td>
 *               <td>3600 seconds</td>
 *           </tr>
 *           <tr>
 *               <td>Refresh token lifetime (seconds)</td>
 *               <td>7200 seconds</td>
 *               <td>600 seconds</td>
 *               <td>172800 seconds</td>
 *           </tr>
 *           <tr>
 *               <td>Authorization code lifetime (seconds)</td>
 *               <td>120 seconds</td>
 *               <td>60 seconds</td>
 *               <td>600 seconds</td>
 *           </tr>
 *           <tr>
 *               <td>Redirection URIs</td>
 *               <td>{{apigee_domain}}/authentication-service/callback, http://localhost</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Token id lifetime (seconds)</td>
 *               <td>3600 seconds</td>
 *               <td>600 seconds</td>
 *               <td>172800 seconds</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h4>Service Account Client</h4>
 *        <table>
 *           <tr>
 *               <th>Parameter</th>
 *               <th>Default Value</th>
 *               <th>Min</th>
 *               <th>Max</th>
 *           </tr>
 *           <tr>
 *               <td>Access Token lifetime (seconds)</td>
 *               <td>3600 seconds</td>
 *               <td>60 seconds</td>
 *               <td>3600 seconds</td>
 *           </tr>
 *           <tr>
 *               <td>Refresh token lifetime (seconds)</td>
 *               <td>0 seconds</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Authorization code lifetime (seconds)</td>
 *               <td>120 seconds</td>
 *               <td>60 seconds</td>
 *               <td>600 seconds</td>
 *           </tr>
 *           <tr>
 *               <td>Redirection URIs</td>
 *               <td>{{apigee_domain}}/authentication-service/callback, http://localhost</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Token id lifetime (seconds)</td>
 *               <td>86400 seconds</td>
 *               <td>600 seconds</td>
 *               <td>172800 seconds</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h3>Authentication settings</h3>
 *        <p>Table lists all the Authentication settings.</p>
 *       <table>
 *           <tr>
 *               <th>Parameter</th>
 *               <th>Default Value</th>
 *               <th>Min</th>
 *               <th>Max</th>
 *           </tr>
 *           <tr>
 *               <td>SMS OTP Length (digits)</td>
 *               <td>6</td>
 *               <td>6</td>
 *               <td>8</td>
 *           </tr>
 *           <tr>
 *               <td>SMTP OTP Length (digits)</td>
 *               <td>6</td>
 *               <td>6</td>
 *               <td>8</td>
 *           </tr>
 *           <tr>
 *               <td>OTP Expiry Time (minutes)</td>
 *               <td>5 minutes</td>
 *               <td>5 minutes</td>
 *               <td>60 minutes</td>
 *           </tr>
 *           <tr>
 *               <td>Request failure lockout mode</td>
 *               <td>true</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>OTP request count</td>
 *               <td>3</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>OTP request duration interval (minutes)</td>
 *               <td>1440 minutes</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Persistent Cookie maximum life (hours)</td>
 *               <td>24 hours</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Persistent Cookie idle timeout (hours)</td>
 *               <td>24 hours</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *       </table>
 *       <br>
 *       <h3>Account lockout settings</h3>
 *        <p>Table lists all the Account lockout settings.</p>
 *       <table>
 *           <tr>
 *               <th>Parameter</th>
 *               <th>Default Value</th>
 *               <th>Min</th>
 *               <th>Max</th>
 *           </tr>
 *           <tr>
 *               <td>Login failure lockout mode</td>
 *               <td>true</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Lockout Duration (minutes)</td>
 *               <td>20 minutes</td>
 *               <td>N/a</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Lockout after number of failures</td>
 *               <td>1</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Lockout warning after number of failures</td>
 *               <td>1</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *           <tr>
 *               <td>Login failure duration (minutes)</td>
 *               <td>10 minutes</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *       </table>
 *       <br>
 *
 *     </section>
 *     <section>
 *      <h4>Failure Status Codes</h3>
 *       <p>
 *       This section describes the Create App Setting Status Code information.
 *       <table>
 *               <tr>
 *                   <th>HTTP Status Code</th>
 *                   <th>Service Error Code</th>
 *                   <th>Error Message</th>
 *               </tr>
 *               <tr>
 *                   <td>403</td>
 *                   <td>UMS_PERMISSION_DENIED</td>
 *                   <td>You do not have permission to view this page</td>
 *               </tr>
 *               <tr>
 *                   <td>500</td>
 *                   <td>UMS_INTERNAL_ERROR</td>
 *                   <td>Internal Server Error</td>
 *               </tr>
 *               <tr>
 *                   <td>503</td>
 *                   <td></td>
 *                   <td>Service Unavailable</td>
 *               </tr>
 *           </table>
 *   </p>
 *   </section
 *   <section>
 *      <h4>Dependent System settings, Platform services & Role Permission</h3>
 *      <p>This section describes the list of dependent system settings & platform services required for functioning of edit Security Setting page.</p>
 *       <h5>System Settings</h4>
 *       <p>Table lists all the dependent system setting(s) defined in configuration service with either global/organization scope.</p>
 *       <table>
 *           <tr>
 *               <th>Key</th>
 *               <th>Type</th>
 *               <th>Value</th>
 *               <th>Scope</th>
 *           </tr>
 *           <tr>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *               <td>N/A</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h5>Platform Service(s)</h4>
 *      <p>Table lists all the dependent platform service(s) with specific version(s) for View/Edit Security Setting.</p>
 *       <table>
 *           <tr>
 *               <th>Service Name</th>
 *               <th>Version</th>
 *           </tr>
 *           <tr>
 *               <td>User Management Service</td>
 *               <td>1.6.0</td>
 *           </tr>
 *       </table>
 *       <br>
 *      <h5>API Role Permission(s)</h4>
 *      <p>Table lists the required API role permissions for creating App Setting page</p>
 *       <table>
 *           <tr>
 *               <th>Feature</th>
 *               <th>API URL</th>
 *               <th>API Method</th>
 *               <th>API Permission</th>
 *               <th>Required</th>
 *           </tr>
 *           <tr>
 *               <td>List Session Token Settings</td>
 *               <td>/session-token-settings</td>
 *               <td>GET</td>
 *               <td>user-management-service.session-token-setting.get</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Update Session Token Settings</td>
 *               <td>/session-token-settings</td>
 *               <td>PUT</td>
 *               <td>user-management-service.session-token-setting.update</td>
 *               <td>Yes</td>
 *           </tr>
 *         <tr>
 *               <td>List Oauth Settings</td>
 *               <td>/oauth2-client-settings</td>
 *               <td>GET</td>
 *               <td>user-management-service.oauth2-client-setting.list</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Update Oauth Settings</td>
 *               <td>/oauth2-client-settings</td>
 *               <td>PATCH</td>
 *               <td>user-management-service.oauth2-client-settings.patch</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>List Account Lockout Settings</td>
 *               <td>/account-lockout-policies</td>
 *               <td>GET</td>
 *               <td>user-management-service.realm.authentication.get</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Update Account Lockout Settings</td>
 *               <td>/account-lockout-policies</td>
 *               <td>PUT</td>
 *               <td>user-management-service.realm.authentication.put</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>List Authentication Settings</td>
 *               <td>/mfa-settings</td>
 *               <td>GET</td>
 *               <td>user-management-service.realm.authentication.get</td>
 *               <td>Yes</td>
 *           </tr>
 *           <tr>
 *               <td>Update Authentication Settings</td>
 *               <td>/mfa-settings</td>
 *               <td>PUT</td>
 *               <td>user-management-service.mfa-setting.update</td>
 *               <td>Yes</td>
 *           </tr>
 *       </table>
 *   </section>
 *   <section>
 *    <p>Sequence Diagram for Security Setting</p>
 *   </section>
 */

interface AccessManagementProps {
  defaultTab?: string;
}

const AccessManagementSettings = ({ defaultTab }: AccessManagementProps) => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const accountLockoutLabel = `${t('T_ACCOUNT_LOCKOUT_SETTINGS')}`;
  const authenticationLabel = `${t('T_AUTHENTICATION_SETTINGS')}`;
  const oAuthLabel = `${t('T_OAUTH_TOKEN_SETTINGS')}`;
  const sessionTokenSettingsLabel = `${t('T_SESSION_TOKEN_SETTINGS')}`;

  const [activeTab, setActiveTab] = useState(defaultTab || sessionTokenSettingsLabel);

  const tabPaths: any = {
    [sessionTokenSettingsLabel]: '/authorization-settings',
    [oAuthLabel]: '/authorization-settings/oauth',
    [authenticationLabel]: '/authorization-settings/authentication',
    [accountLockoutLabel]: '/authorization-settings/accountlockout'
  };

  const tabConfig = [
    {
      label: sessionTokenSettingsLabel,
      component: <SessionTokenSettings />
    },
    {
      label: oAuthLabel,
      component: <OauthTokenSettings />
    },
    {
      label: authenticationLabel,
      component: <AuthenticationSettings />
    },
    {
      label: accountLockoutLabel,
      component: <AccountLockOutPolicySettings />
    }
  ];

  const ChangeTab = (tab: string) => {
    navigate(tabPaths[tab]);
    setActiveTab(tab);
  };

  return (
    <div style={{ width: '100%' }}>
      <TabDrawer
        borderBottom={false}
        tabConfig={tabConfig}
        activeTab={activeTab}
        setActiveTab={ChangeTab}
        style={{
          width: '100%',
          background: '#fff',
          height: '48px',
          borderBottom: '0.5px solid',
          paddingTop: '2px'
        }}
        childStyle={{ height: 'calc(100vh - 9rem)' }}
      />
    </div>
  );
};

export default AccessManagementSettings;
